import React, { useMemo, useState } from "react";
import { Field, Formik as FormikBase } from "formik";
import { useNavigate } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import {
  validCreatePasswordForm,
  validPassConfPass,
} from "@/helpers/validations";
import { passwordMDInputFormik, textMDInputFormik } from "@/helpers/formInputs";
import LoadingButton from "@/components/loaders/LoadingButton";
import { authService } from "@/features/auth/services/authService";
import { logger } from "@/helpers/loggerHelper";
import { HTTP_STATUS } from "@/config/constants";
import { encodePassword } from "@/helpers/utils";

const Formik = FormikBase as unknown as React.ComponentType<any>;

type CreatePasswordValues = {
  confirmPassword: string;
  first_name?: string;
  last_name?: string;
  password: string;
};

export default function CreatePasswordForm({
  token,
  email,
  subscribe,
  first_name,
  last_name,
  isNameRequired,
}: {
  email?: string;
  first_name?: string;
  isNameRequired?: boolean;
  last_name?: string;
  subscribe?: boolean | string | string[];
  token?: string;
}) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const shouldAskForName = isNameRequired
    ? true
    : first_name || last_name
      ? false
      : true;

  async function createPassword(data: any) {
    setIsLoading(true);
    try {
      const res = await authService.setNewPassword(data);
      if (res.status === HTTP_STATUS.CREATED) {
        toastr.success(
          "Success",
          `${shouldAskForName ? "Account" : "Password"} created successfully`
        );
        navigate(
          `/login?email=${data?.email}${subscribe ? "&subscribe=true" : ""}`,
          { replace: true }
        );
      }
    } catch (err) {
      logger.error("Error in createPassword", { err });
    } finally {
      setIsLoading(false);
    }
  }

  const onSubmitForm = (values: CreatePasswordValues) => {
    const reqData = {
      token,
      email,
      isNameRequired,
      ...values,
      password: encodePassword(values.password),
      confirmPassword: encodePassword(values.confirmPassword),
    };
    createPassword(reqData);
  };

  const initialValues = useMemo(() => {
    // Initialize form with empty strings (not credentials)
    const initialV: CreatePasswordValues = {
      password: "",
      confirmPassword: "",
    };
    if (shouldAskForName) {
      initialV.first_name = "";
      initialV.last_name = "";
    }
    return initialV;
  }, [shouldAskForName]);

  return (
    <div className="create-password-form">
      <h2 className="heading">{`Create ${
        shouldAskForName ? "your account" : "your password"
      }`}</h2>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmitForm}
        validate={
          shouldAskForName ? validCreatePasswordForm : validPassConfPass
        }
      >
        {({ handleSubmit }: any) => {
          return (
            <form onSubmit={handleSubmit}>
              {shouldAskForName ? (
                <>
                  <Field
                    name="first_name"
                    label="First Name"
                    component={textMDInputFormik}
                    autoFocus={true}
                    autoComplete="new-password"
                  />
                  <Field
                    name="last_name"
                    label="Last Name"
                    component={textMDInputFormik}
                    autoComplete="new-password"
                  />
                </>
              ) : null}

              <Field
                name="password"
                label="Password"
                component={passwordMDInputFormik}
                autoFocus={shouldAskForName ? false : true}
                autoComplete="new-password"
              />
              <Field
                name="confirmPassword"
                label="Confirm Password"
                component={passwordMDInputFormik}
                autoComplete="new-password"
              />

              <div className="mt-4">
                <LoadingButton
                  type="submit"
                  text={`Create ${shouldAskForName ? "Account" : "Password"}`}
                  isLoading={isLoading}
                />
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}
